/**
 * <flex-pdp-upsell>
 *
 * Flex-PDP buy-button upsell. Lets the customer tick add-on products that get
 * added to the cart as SEPARATE line items in the same Add-to-Cart action.
 *
 * The flex-pdp product form (<product-form>, snippets/form.product.liquid)
 * dispatches a cancelable `product-form:submit:before` event carrying the
 * FormData (detail.form) and then awaits a microtask, so synchronous mutations
 * to that FormData are picked up. Because the form dispatches on ITSELF, the
 * event bubbles up to document (not down to this descendant), so we listen on
 * document and act on our own checked boxes.
 *
 * No base-theme JS is touched; this is fully self-contained.
 */
if (!customElements.get('flex-pdp-upsell')) {
  customElements.define(
    'flex-pdp-upsell',
    class FlexPdpUpsell extends HTMLElement {
      connectedCallback() {
        this.onSubmitBefore = this.handleSubmitBefore.bind(this);
        this.onSubmitSuccess = this.handleSubmitSuccess.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        document.addEventListener('product-form:submit:before', this.onSubmitBefore);
        document.addEventListener('product-form:submit:success', this.onSubmitSuccess);
        this.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        document.removeEventListener('product-form:submit:before', this.onSubmitBefore);
        document.removeEventListener('product-form:submit:success', this.onSubmitSuccess);
        this.removeEventListener('keydown', this.onKeydown);
      }

      /**
       * Inside a form, pressing Enter on a checkbox submits the form rather than
       * toggling it. Customers expect Enter to toggle the upsell, so intercept it.
       */
      handleKeydown(event) {
        if (event.key !== 'Enter') return;
        const target = event.target;
        if (target && target.matches('[data-upsell-checkbox]')) {
          event.preventDefault();
          target.checked = !target.checked;
        }
      }

      checkedBoxes() {
        return Array.from(this.querySelectorAll('[data-upsell-checkbox]')).filter(
          (cb) => cb.checked && cb.getAttribute('data-upsell-variant-id')
        );
      }

      /**
       * Rewrite the single-product payload (id + quantity) into an items[] array
       * so the main product and any checked upsells are added together as
       * separate line items in one cart/add.js request.
       */
      handleSubmitBefore(event) {
        const form = event.detail && event.detail.form;
        if (!form) return;

        const checked = this.checkedBoxes();
        if (!checked.length) return;

        const mainId = form.get('id');
        if (!mainId) return; // not a standard single-product payload; leave as-is

        const mainQty = form.get('quantity') || 1;
        const sellingPlan = form.get('selling_plan');

        // Preserve any line-item properties on the main product
        const props = [];
        for (const [key, value] of form.entries()) {
          if (key.indexOf('properties[') === 0) props.push([key, value]);
        }

        // Strip the keys we're about to move into items[0]
        form.delete('id');
        form.delete('quantity');
        form.delete('selling_plan');
        props.forEach(([key]) => form.delete(key));

        // items[0] = the main product
        form.append('items[0][id]', mainId);
        form.append('items[0][quantity]', mainQty);
        if (sellingPlan) form.append('items[0][selling_plan]', sellingPlan);
        props.forEach(([key, value]) => {
          const inner = key.slice('properties['.length, -1);
          form.append(`items[0][properties][${inner}]`, value);
        });

        // items[1..n] = checked upsells (qty 1 each)
        checked.forEach((cb, i) => {
          form.append(`items[${i + 1}][id]`, cb.getAttribute('data-upsell-variant-id'));
          form.append(`items[${i + 1}][quantity]`, 1);
        });
      }

      /**
       * After a successful add, surface the feedback message for each checked
       * slot and reset it, so the message reflects this submission only.
       */
      handleSubmitSuccess() {
        const checked = this.checkedBoxes();
        if (!checked.length) return;

        checked.forEach((cb) => {
          const item = cb.closest('.flex-pdp-upsell__item');
          const feedback = item && item.querySelector('[data-upsell-feedback]');
          if (feedback) feedback.hidden = false;
          cb.checked = false;
        });
      }
    }
  );
}
